import { useCallback } from 'react';
import { getFirebaseDb } from '@/shared/services/firebase/config';
import { doc, writeBatch } from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS } from '@/shared/constants';
import { logger } from '@/shared/utils/logger';
import { calculateNewOrders, reorganizeAfterDrag, OrderableItem } from '@/shared/utils/orderManager';
import { handleFirebaseError } from '@/shared/services/firebase/errors';

interface UseOrderManagerOptions {
  museumId: string;
  collectionPath: string[]; // Ex: [FIRESTORE_COLLECTIONS.EXHIBITIONS] ou [FIRESTORE_COLLECTIONS.EXHIBITIONS, exhibitionId, FIRESTORE_COLLECTIONS.WORKS]
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook réutilisable pour gérer l'ordre des éléments (expositions, œuvres, etc.)
 */
export function useOrderManager<T extends OrderableItem>({
  museumId,
  collectionPath,
  onSuccess,
  onError,
}: UseOrderManagerOptions) {
  /**
   * Change l'ordre d'un élément et met à jour automatiquement les autres
   */
  const changeOrder = useCallback(
    async (items: T[], itemId: string, newOrder: number): Promise<boolean> => {
      const db = getFirebaseDb();
      if (!db) {
        const error = 'Firebase DB not initialized';
        logger.error(error);
        onError?.(error);
        return false;
      }

      try {
        const newOrders = calculateNewOrders(items, itemId, newOrder);

        // Mettre à jour dans Firestore avec un batch
        const batch = writeBatch(db);

        newOrders.forEach((order, id) => {
          const itemRef = doc(db, FIRESTORE_COLLECTIONS.MUSEUMS, museumId, ...collectionPath, id);
          batch.update(itemRef, { order });
        });

        await batch.commit();
        onSuccess?.();
        return true;
      } catch (error) {
        const errorMessage = handleFirebaseError(error);
        logger.error('Erreur lors de la mise à jour de l\'ordre:', error);
        onError?.(errorMessage);
        return false;
      }
    },
    [museumId, collectionPath, onSuccess, onError]
  );

  /**
   * Réorganise les éléments après un drag and drop (insertion entre les cartes).
   * @param insertAfter Si true, insère après la cible ; si false, insère avant.
   * @param options.skipSuccessCallback Si true, n'appelle pas onSuccess après le commit (pour mise à jour optimiste).
   */
  const reorderAfterDrag = useCallback(
    async (
      items: T[],
      draggedItemId: string,
      targetItemId: string,
      insertAfter?: boolean,
      options?: { skipSuccessCallback?: boolean }
    ): Promise<boolean> => {
      const db = getFirebaseDb();
      if (!db) {
        const error = 'Firebase DB not initialized';
        logger.error(error);
        onError?.(error);
        return false;
      }

      try {
        const newOrders = reorganizeAfterDrag(items, draggedItemId, targetItemId, insertAfter);

        const batch = writeBatch(db);

        newOrders.forEach((order, id) => {
          const itemRef = doc(db, FIRESTORE_COLLECTIONS.MUSEUMS, museumId, ...collectionPath, id);
          batch.update(itemRef, { order });
        });

        await batch.commit();
        if (!options?.skipSuccessCallback) {
          onSuccess?.();
        }
        return true;
      } catch (error) {
        const errorMessage = handleFirebaseError(error);
        logger.error('Erreur lors de la réorganisation:', error);
        onError?.(errorMessage);
        return false;
      }
    },
    [museumId, collectionPath, onSuccess, onError]
  );

  return {
    changeOrder,
    reorderAfterDrag,
  };
}
